import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { fetchAppConfig } from '../utils/appConfigService';
import { generateThemeFromPrimaryColor } from '../utils/themeUtils';

// 默认主题设置
export const DEFAULT_THEME = {
    primary: '#c0a801',
    inversePrimary: '#dbc84d',
    inverseSurface: '#20201e',
    textOnPrimary: '#ffffff',
    secondaryContainer: '#f6efba'
};

// 导出 THEME 对象以供组件使用
export const THEME = { ...DEFAULT_THEME };

// Create context for app configuration
const AppConfigContext = createContext();

/**
 * Custom hook to use app configuration
 * @returns {Object} The app configuration and loading state
 */
export const useAppConfig = () => {
    const context = useContext(AppConfigContext);
    if (!context) {
        throw new Error('useAppConfig must be used within an AppConfigProvider');
    }
    return context;
};

/**
 * Provider component for app configuration
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.appcode - Application code for configuration
 * @param {boolean} props.useMock - Whether to use mock data (for development)
 */
export const AppConfigProvider = ({ children, appcode = 'einvoice', useMock = false }) => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [theme, setTheme] = useState(DEFAULT_THEME);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const lastRefreshTimestamp = useRef('');
    const isInitialLoad = useRef(true);

    // Function to load configuration
    const loadConfig = async (showLoading = true) => {
        try {
            // Only show loading indicator on initial load, not on refreshes
            if (showLoading) {
                setLoading(true);
            }

            // Fetch configuration data
            const configData = await fetchAppConfig(appcode, useMock);
            setConfig(configData);

            // Generate theme based on primary color
            if (configData?.settings?.themeSetting?.primaryColor) {
                const generatedTheme = generateThemeFromPrimaryColor(
                    configData.settings.themeSetting.primaryColor
                );

                // Update THEME object for backward compatibility
                Object.keys(THEME).forEach(key => {
                    THEME[key] = generatedTheme[key];
                });

                setTheme(generatedTheme);
            }

            setError(null);
        } catch (err) {
            console.error('Failed to load app configuration:', err);
            setError(err.message || 'Failed to load app configuration');
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    // Initial load when component mounts
    useEffect(() => {
        if (isInitialLoad.current) {
            // First time load - show loading indicator
            loadConfig(true);
            isInitialLoad.current = false;
        } else {
            // Subsequent refreshes - don't show loading indicator
            loadConfig(false);
        }
    }, [appcode, useMock, refreshTrigger]);

    // Listen for config update events
    useEffect(() => {
        // Function to handle config update events
        const handleConfigUpdate = (event) => {
            if (event.detail && event.detail.appcode === appcode) {
                console.log('AppConfigProvider: Received config update event, refreshing...');
                setRefreshTrigger(prev => prev + 1);
            }
        };

        // Function to handle localStorage change for configs
        const handleStorageChange = (e) => {
            if (e.key === 'app-config-updated') {
                const newTimestamp = e.newValue;

                // Only refresh if the timestamp is different from the last one we processed
                if (newTimestamp && newTimestamp !== lastRefreshTimestamp.current) {
                    console.log('AppConfigProvider: Detected storage change, refreshing...');
                    lastRefreshTimestamp.current = newTimestamp;
                    setRefreshTrigger(prev => prev + 1);
                }
            }
        };

        // Add event listeners
        window.addEventListener('app-config-updated', handleConfigUpdate);
        window.addEventListener('storage', handleStorageChange);

        // Clean up
        return () => {
            window.removeEventListener('app-config-updated', handleConfigUpdate);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [appcode]);

    // Check once on mount if there's a pending refresh in localStorage
    useEffect(() => {
        const storedTimestamp = localStorage.getItem('app-config-updated');
        if (storedTimestamp && storedTimestamp !== lastRefreshTimestamp.current) {
            lastRefreshTimestamp.current = storedTimestamp;
            // Only trigger a refresh if this is not the initial mount (refreshTrigger > 0)
            if (refreshTrigger > 0) {
                setRefreshTrigger(prev => prev + 1);
            }
        }
    }, []);

    // This is the value object we'll provide to consumers
    const contextValue = {
        config,
        loading,
        error,
        appcode,
        theme,
        // Provide a refreshConfig function that doesn't show loading indicators
        refreshConfig: () => {
            setRefreshTrigger(prev => prev + 1);
        }
    };

    return (
        <AppConfigContext.Provider value={contextValue}>
            {/* Only show loading indicator on initial load, not on refreshes */}
            {loading && isInitialLoad.current ? <div>Loading...</div> : children}
        </AppConfigContext.Provider>
    );
};

export default AppConfigProvider; 